/**
 * The Mountaya error model.
 *
 * Every non-2xx response carries the same envelope (`{ errors: [{ message, path?,
 * extensions: { code } }] }`) across REST, the Tile API and the Data API, because the API
 * renders one error into all three. This module turns that envelope into typed classes so a
 * caller branches on a class or a code rather than on a status number.
 *
 * A 402 says something about the organization's agreement or its billing: a product the
 * organization does not hold, a call past a ceiling its agreement sets, an organization billed by
 * card with no payment method on file, which the Data API refuses, or invoices still owed, which
 * keep its Data API subscription from opening. Every 402 is one class here, and each is answered by
 * changing the agreement or the billing details, not in code. Its entry carries
 * `PAYMENT_REQUIRED`, or `LIMIT_REACHED` where the call is past a ceiling the agreement sets (its
 * seats, its published itineraries, its published zones).
 *
 * A 403 says what the caller may not do whatever the agreement holds: a scope its key lacks,
 * another organization, a calling page the organization does not allow, a member without the role,
 * or a ceiling no agreement changes (the itineraries a personal space keeps, the invitations an
 * organization may hold waiting), whose entry carries `LIMIT_REACHED`. A limit or a monthly
 * allowance that is spent is a 429. Those three are routinely conflated and each wants a different
 * response from a caller, so none of them shares a class with another.
 *
 * Every error the API answered names its request: the answer's `X-Request-Id`, the one thing a
 * caller quotes to support, and the rate-limit state its headers describe.
 */

import type { ErrorCode as DataErrorCode } from "../../data/schema.gen.js";
import type { HTTPError as RestErrorEntry } from "../../rest/schema.gen.js";
import type { HTTPError as TileErrorEntry } from "../../tiles/schema.gen.js";
import { parseRateLimit, type RateLimitInfo } from "./rateLimit.js";

/**
 * ErrorCode is the canonical machine-readable code the API stamps on a failure: the closed
 * vocabulary each surface declares for `extensions.code`, joined.
 *
 * **Derived, never listed.** Each surface's contract declares the codes it sends (the two REST
 * bundles as the enum on their error entry, the Data API as its `ErrorCode` enum), and this is
 * their union, read off the generated types, so a code the contract adds reaches a caller
 * switching on this type the day the client is regenerated.
 *
 * It stays open, with `string & {}` beside the declared codes, so an unrecognized code from a
 * newer API release widens to `string` instead of failing a parse (a client should never break
 * because it met a code it has not been taught yet), while an editor still offers every
 * declared one.
 */
export type ErrorCode =
  | RestErrorEntry["extensions"]["code"]
  | TileErrorEntry["extensions"]["code"]
  | DataErrorCode
  | (string & {});

/**
 * ErrorPath is the field an error entry is about, as the contract spells it: a path of names
 * with numeric array indices (`["request", "body", "segments", 2, "geojson"]`).
 *
 * The index stays a number on the wire, so it stays a number here: coercing it to a string
 * would discard the most specific thing the API can tell a caller about a rejected request.
 * Read off the REST contract's own error entry, which is where the shape is declared; the Data
 * API's SDL describes the same shape for its `extensions.path` in prose, because GraphQL has no
 * way to type an error.
 */
type ErrorPath = Readonly<NonNullable<RestErrorEntry["path"]>>;

/**
 * ErrorEntry is one entry of the API's error envelope.
 *
 * The field the entry is about is carried in a different place per surface, and both are typed
 * here so a caller reading a failure does not have to know which surface it came from to find
 * them:
 *
 * - **REST and the Tile API** carry it on the entry itself, as `path`, from the root of the
 *   request: `["request", "body", "name"]`, `["request", "query", "limit"]`.
 * - **The Data API** carries it under `extensions.path`, from the root of the operation's
 *   input: `["input", "waypoints", 0]`. The entry's own `path` there is GraphQL's, and names the
 *   response field that failed rather than the input that did.
 */
export interface ErrorEntry {
  /** The sentence the API wrote, in the client's `language` where the API localizes it. */
  readonly message: string;
  /**
   * On REST and the Tile API, the request field the entry is about; on the Data API, GraphQL's
   * own path to the response field that failed.
   */
  readonly path?: ErrorPath | undefined;
  /** The machine-readable half of the entry: its code, and on the Data API its input field. */
  readonly extensions?:
    | {
        /** The entry's code, which a caller switches on rather than on the message. */
        readonly code?: ErrorCode | undefined;
        /** On the Data API, the input field a validation failure is about. */
        readonly path?: ErrorPath | undefined;
      }
    | undefined;
}

/**
 * MountayaError is the base of every error this package throws. Catching it separates a
 * platform failure from a bug in the calling program.
 *
 * **Every class in the family declares its `name` as a string literal**, and none of them
 * reads it off the constructor. `this.name = new.target.name` is the obvious spelling and it
 * is wrong in a published package: a class name is an identifier, and a consumer's minifier
 * renames identifiers. Vite, Next and esbuild all ship production defaults that do, so
 * `error.name === "MountayaNotFoundError"` holds in the consumer's development build, and in
 * their production build the same error reports `name: "a"`. A literal is data, so it
 * survives; and it is what makes `name` usable at all where `instanceof` is not, which is a
 * real case rather than a hypothetical one: a package resolved once as ESM and once as CJS
 * loads two class objects, and an error thrown from one fails `instanceof` against the other.
 *
 * `errors.test.ts` derives the check from the barrel, so a class added without a literal
 * fails there rather than in a consumer's minified bundle.
 */
export class MountayaError extends Error {
  override readonly name: string = "MountayaError";

  /**
   * The `Idempotency-Key` the call that raised this error sent on its attempts, the caller's own
   * or the one the client drew for it, and undefined where it sent none: a read, an operation
   * that declares no key, a call refused before its first attempt, and a runtime with no
   * cryptographic random source.
   *
   * A write that carried one may have been made whatever the error says: an answer that never
   * arrived, a 5xx and a body that broke off do not tell. Send the same call again with this key
   * as its `idempotencyKey`, never a new one: the API answers a key it has seen with the answer it
   * kept, or runs the write where it made none, so the write is made once. A new key is a new
   * write, made a second time where the first one landed.
   */
  readonly idempotencyKey?: string | undefined;

  // The constructor narrows the signature rather than adding behaviour: `Error`'s `message`
  // is optional and this family's is not. Dropping it makes
  // `new MountayaConfigurationError()` compile, which is a thrown value carrying nothing a
  // caller can read.
  /**
   * @param message - what went wrong and what to do about it, naming the call where there is one.
   * @param options - the standard error options, for the `cause` behind it, and the
   *   `idempotencyKey` the call sent, where it sent one.
   */
  constructor(
    message: string,
    options?: (ErrorOptions & { readonly idempotencyKey?: string | undefined }) | undefined,
  ) {
    super(message, options);
    this.idempotencyKey = options?.idempotencyKey;
  }
}

/**
 * MountayaConfigurationError is thrown when the client is set up in a way that cannot work, and
 * before any request is made wherever that can be known: no credential (an empty or blank key
 * included), a credential whose prefix does not match its slot, a secret key in a browser, or an
 * option outside its range (a `timeoutMs`, a `retry` member, a `baseUrls` entry). A call refuses
 * the same way, before its first attempt, when its credential cannot make it, it asks for
 * `text/event-stream` (which the openers of `@mountaya/client/events` read), its own `retry` or
 * `timeoutMs` is out of range, or its body is one `JSON.stringify` refuses (a cycle, a
 * `BigInt`). An operation that requires a session is refused the same way once its wait for the
 * first session token ends with none, with the mint's error as its `cause`. A stream open raises
 * one once its answer arrives when the runtime's fetch exposes no response body to read the
 * stream from, which only the response can show.
 *
 * It is deliberately not an `APIError`: the API refused nothing, so there is no status to report
 * and no retry that could help.
 */
export class MountayaConfigurationError extends MountayaError {
  override readonly name = "MountayaConfigurationError";
}

/**
 * describeType spells the type a refusal names a value by, with its article (`a string`,
 * `an object`, `null`), for a value a refusal must never quote: a credential, or options that may
 * hold one, since a key is a secret whatever type it arrived as.
 */
export const describeType = (value: unknown): string => {
  if (value === null || value === undefined) {
    return String(value);
  }

  const type = Array.isArray(value) ? "array" : typeof value;

  return /^[aeiou]/.test(type) ? `an ${type}` : `a ${type}`;
};

/**
 * describeValue spells an option's value the way a refusal quotes it: a string in quotes, so an
 * empty or padded one is visible, and anything else as `String` prints it. A value `String`
 * cannot print (an object with no prototype, one whose `toString` throws) is named by its type,
 * as {@link describeType} spells it, so the refusal is still the one that names the option.
 */
const describeValue = (value: unknown): string => {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }

  try {
    return String(value);
  } catch {
    return describeType(value);
  }
};

/**
 * refuseOption builds the error an option outside its range throws: the option by the path a
 * caller writes it at (`timeoutMs`, `retry.maxRetries`, `baseUrls.rest`), the value, and what to
 * pass instead.
 *
 * `context` names the call a per-call option was passed to, and is omitted for a client's own
 * options, which belong to no call.
 */
export const refuseOption = (
  path: string,
  value: unknown,
  remedy: string,
  context?: string,
): MountayaConfigurationError =>
  new MountayaConfigurationError(
    `${context === undefined ? "" : `${context}: `}${path} is ${describeValue(value)}; ${remedy}`,
  );

/**
 * MountayaConnectionError wraps a transport failure: DNS, TLS, a dropped socket, an attempt
 * that had no response within its `timeoutMs` (the call's, or else the client's), a wait for
 * the first session token that the mint did not answer within it, a stream open with no
 * response within its bound, a stream that delivered nothing (not even the heartbeat comment
 * the API writes to every live stream) for its stall window, a response whose body broke off
 * before it was complete, a success whose body is not JSON on a call that answers JSON (on REST,
 * the Tile API and the Data API alike), or an ok answer to a stream open that carries no body.
 * The last two are what something on the network path (a captive portal, a proxy) sends in the
 * API's place. Its message names the call, and says which of those it was where the transport
 * can tell.
 *
 * A caller's own abort is not one: it rejects with the signal's own reason, never wrapped, or
 * with an `AbortError` where the signal carries none.
 *
 * The original is carried on the standard `Error.cause` rather than on a field of our
 * own, so every tool that already knows how to unwrap a chained error (Node's inspector,
 * test reporters, error trackers) finds it without being taught about this package.
 */
export class MountayaConnectionError extends MountayaError {
  override readonly name = "MountayaConnectionError";

  /**
   * @param message - the call, and which transport failure it met where the transport can tell.
   * @param cause - the runtime's own failure, carried on `Error.cause`.
   * @param idempotencyKey - the `Idempotency-Key` the call sent, where it sent one.
   */
  constructor(message: string, cause?: unknown, idempotencyKey?: string | undefined) {
    super(message, { cause, idempotencyKey });
  }
}

/**
 * ErrorMetadata is the envelope's `extensions.metadata`: what a failure says about the state
 * that refused it, in the shape the operation's own contract declares.
 *
 * It is what tells apart two answers that share a status and a code. A `409` refused by a held
 * lock names the member holding it at `holder.id` (`RestSchema.ResponseMetadataLockConflict`),
 * a `409` refused because another write landed first carries none, and a name conflict names the
 * resource holding the name. Typed as a record of unknown values rather than as one of those
 * shapes, because which shape a failure carries is the operation's to declare: a caller narrows
 * it against the operation's schema, reading each member before trusting it.
 */
export interface ErrorMetadata {
  readonly [member: string]: unknown;
}

/**
 * ApiErrorDetails is what an error the API answered carries beside its status and its entries,
 * each where the answer sent it: what {@link errorForStatus} and the constructors of the family
 * take fourth.
 */
export interface ApiErrorDetails {
  /**
   * The answer's `ETag`, exactly as sent: the tag the resource carries now, which a
   * {@link MountayaPreconditionFailedError} carries and every other class leaves out.
   */
  readonly etag?: string | undefined;
  /** The `Idempotency-Key` the call sent, where it sent one: see {@link MountayaError}. */
  readonly idempotencyKey?: string | undefined;
  /** The envelope's `extensions.metadata`, exactly as sent: see {@link ErrorMetadata}. */
  readonly metadata?: ErrorMetadata | undefined;
  /** The rate-limit state the answer's `RateLimit-Policy` and `RateLimit` describe. */
  readonly rateLimit?: RateLimitInfo | undefined;
  /** The answer's `X-Request-Id`, exactly as sent. */
  readonly requestId?: string | undefined;
  /** The whole seconds the answer's `Retry-After` names, where it sent one it can read. */
  readonly retryAfter?: number | undefined;
}

/**
 * MountayaApiError is any error the API itself reported, carrying the HTTP status, the parsed
 * envelope entries, the envelope's metadata where it sent any, the answer's request id, the
 * rate-limit state it describes and the wait its `Retry-After` names.
 *
 * `code` is the first entry's code, which is the classification for the response as a
 * whole; `errors` holds every entry for a caller that needs the field-level detail of a
 * multi-error validation failure.
 */
export class MountayaApiError extends MountayaError {
  override readonly name: string = "MountayaApiError";

  /** The HTTP status the response carried. */
  readonly status: number;
  /** Every entry of the envelope, in the order the API wrote them; empty for a bodiless answer. */
  readonly errors: readonly ErrorEntry[];

  /**
   * The envelope's `extensions.metadata`, exactly as sent, or undefined where the response
   * carried none: see {@link ErrorMetadata}.
   */
  readonly metadata?: ErrorMetadata | undefined;

  /**
   * The rate-limit state the answer's `RateLimit-Policy` and `RateLimit` describe, or undefined
   * where they describe none, as on every Map Studio API answer.
   */
  readonly rateLimit?: RateLimitInfo | undefined;

  /**
   * The answer's `X-Request-Id`, exactly as sent: 32 lowercase hex characters the API recorded
   * the request under, and the one thing to quote to support. Undefined where the answer sent
   * none; the client never makes one up.
   */
  readonly requestId?: string | undefined;

  /**
   * How many seconds the answer asked the caller to wait before asking again, from its
   * `Retry-After` header, read as whole seconds or an HTTP date; undefined where the header was
   * absent or neither.
   *
   * Every 429 names one, the wait a spent limit asks for ({@link MountayaRateLimitError}). A 503
   * names one where the API knows when it can answer, and a 409 whose `Idempotency-Key` is still
   * in use names the second the first request may take. The client waits each out itself where it
   * repeats the call and the wait fits its `retry.maxWaitMs`; an error that arrives carrying one
   * is the wait a caller schedules the next call from, and one carrying none says nothing of when.
   */
  readonly retryAfter?: number | undefined;

  /**
   * @param message - the message {@link failureMessage} spells for the call.
   * @param status - the HTTP status the response carried.
   * @param errors - the envelope's entries, the first of which classifies the response.
   * @param details - the envelope's metadata, the request id, the rate-limit state and the wait
   *   `Retry-After` names, where the answer sent them, and the `Idempotency-Key` the call sent,
   *   where it sent one.
   */
  constructor(
    message: string,
    status: number,
    errors: readonly ErrorEntry[],
    details: ApiErrorDetails = {},
  ) {
    super(message, { idempotencyKey: details.idempotencyKey });
    this.status = status;
    this.errors = errors;
    this.metadata = details.metadata;
    this.rateLimit = details.rateLimit;
    this.requestId = details.requestId;
    this.retryAfter = details.retryAfter;
  }

  /** The classifying code of the response, taken from its first entry. */
  get code(): ErrorCode | undefined {
    return this.errors[0]?.extensions?.code;
  }

  /** Reports whether any entry carries the given code. */
  hasCode(code: ErrorCode): boolean {
    return this.errors.some((entry) => entry.extensions?.code === code);
  }
}

/** 400: the request was malformed or a field failed validation. */
export class MountayaBadRequestError extends MountayaApiError {
  override readonly name = "MountayaBadRequestError";
}

/**
 * 401: the credential is missing, malformed, unknown, rotated or disabled. A session token that
 * expired, or was minted for another key, is one, and so is a call to an operation that acts as
 * the user its session token names, under a token minted without `user_id`: such a token names
 * no user to act as. A call to a `studio-embedded` operation under a token minted for another
 * surface than `studio-embedded` is one too.
 */
export class MountayaAuthenticationError extends MountayaApiError {
  override readonly name = "MountayaAuthenticationError";
}

/**
 * 402: the organization does not hold the product the call needs, such as Map Studio for a write
 * into one of its itineraries; the call is past what its agreement covers, such as its published
 * itinerary limit or its seats; or, on the Data API, a self-serve organization has no payment
 * method on file, or still owes invoices its payment method could not pay, which keep a
 * subscription from opening. The message names which, in the API's words: a product or a ceiling
 * of the agreement is changed with Mountaya (https://mountaya.com/en/contact/), and a payment
 * method is added, and what is owed paid, in the organization's billing settings.
 *
 * Its entry carries `PAYMENT_REQUIRED`, or `LIMIT_REACHED` for a call past a ceiling of the
 * agreement, which `hasCode("LIMIT_REACHED")` reads.
 */
export class MountayaBillingError extends MountayaApiError {
  override readonly name = "MountayaBillingError";
}

/**
 * 403: the caller may not do this, whatever the organization's agreement holds. A scope its key
 * does not carry, a resource outside its organization, or a page the organization does not allow
 * to call. A create past the itineraries a personal space keeps is one too, and so is an invitation
 * past the ones an organization may hold waiting, each entry carrying `LIMIT_REACHED`: no
 * agreement changes either ceiling. So is a call that acts as a member whose session token names a
 * user without the role the call asks.
 *
 * A product the organization does not hold, and a ceiling its agreement sets, are a
 * {@link MountayaBillingError} instead: the agreement is what answers them, where a 403 is answered
 * by the key, the page or the member.
 */
export class MountayaForbiddenError extends MountayaApiError {
  override readonly name = "MountayaForbiddenError";
}

/** 404: no such resource, or none the caller is allowed to know exists. */
export class MountayaNotFoundError extends MountayaApiError {
  override readonly name = "MountayaNotFoundError";
}

/**
 * 405: the path exists and does not answer that method.
 *
 * Distinct from 404 because the remedy is: a 404 means the resource is wrong and a 405 means
 * the verb is, and a caller that folded them together would retry a correct path with the same
 * wrong method.
 */
export class MountayaMethodNotAllowedError extends MountayaApiError {
  override readonly name = "MountayaMethodNotAllowedError";
}

/**
 * 409: the write conflicts with the current state of the resource: a lock another member holds,
 * a write that landed first, a name already taken, or a state that forbids the act, as the shape
 * of an itinerary's chain forbids deleting one of its interior segments.
 *
 * Two 409s answer the `Idempotency-Key` a write carried rather than the write itself, and each
 * raises a subclass of this class: {@link MountayaIdempotencyKeyInUseError}, whose first request
 * is still running, and {@link MountayaIdempotentAnswerUnavailableError}, whose write was made.
 */
export class MountayaConflictError extends MountayaApiError {
  // Typed as a string rather than as its literal, so each subclass can declare its own.
  override readonly name: string = "MountayaConflictError";
}

/**
 * KEY_IN_USE_CODE is the code of a 409 whose `Idempotency-Key` is still in use: the first request
 * with that key has not finished.
 */
export const KEY_IN_USE_CODE = "IDEMPOTENCY_KEY_IN_USE";

/**
 * MountayaIdempotencyKeyInUseError is the 409 whose first entry is coded `IDEMPOTENCY_KEY_IN_USE`:
 * **the first request with this key is still running; send the same call again with the same
 * key.**
 *
 * The client asks again itself, with the same key, after the second the answer names, for as long
 * as the call's `retry.maxWaitMs` lasts, and raises this once that budget is spent with the first
 * request still running. Its `idempotencyKey` is the key: send the same call again with it as the
 * call's `idempotencyKey`, and the API answers with whatever the first request is answered with.
 * Never send it with a new key, which is a second write, made beside the first.
 *
 * A {@link MountayaConflictError} by its status, so a caller handling every 409 alike still meets
 * it, and a class of its own so that one handling a conflict can tell a write still running from
 * one that was refused.
 */
export class MountayaIdempotencyKeyInUseError extends MountayaConflictError {
  override readonly name = "MountayaIdempotencyKeyInUseError";
}

/**
 * IDEMPOTENT_ANSWER_UNAVAILABLE_CODE is the code of the 409 a repeat of an `Idempotency-Key` is
 * answered with when the write it carried was made and the first answer to it cannot be given
 * again, for one of three reasons: too large for the API to keep whole, a 5xx the API answered
 * after the write, or an answer carrying a credential shown only once, which is never kept.
 */
export const IDEMPOTENT_ANSWER_UNAVAILABLE_CODE = "IDEMPOTENCY_ANSWER_UNAVAILABLE";

/**
 * IdempotentAnswer is what the 409 coded `IDEMPOTENCY_ANSWER_UNAVAILABLE` says of the answer the
 * write was first given, read off its `extensions.metadata` member by member: each is undefined
 * where the metadata does not state it in the type the contract declares.
 */
export interface IdempotentAnswer {
  /**
   * The status the write was first answered with: `201` for a write that created something,
   * `200` for one that changed it, the 2xx of an answer carrying a credential shown only once,
   * or the 5xx a request failed with after its write was made.
   */
  readonly status?: number | undefined;
  /**
   * The path of what the write created, relative to the API's host: the `Location` its `201`
   * carried, or, for a create whose operation says so (`postOrganization`,
   * `postOrganizationInvitation`), the path of what it made when the request failed with a 5xx
   * once that was made. Undefined on every other answer.
   */
  readonly location?: string | undefined;
  /** The `ETag` that answer carried, the version of the resource the write left. */
  readonly etag?: string | undefined;
}

/**
 * MountayaIdempotentAnswerUnavailableError is the 409 whose first entry is coded
 * `IDEMPOTENCY_ANSWER_UNAVAILABLE`: **the write was made; re-read the resource.**
 *
 * The call repeated an `Idempotency-Key` (the client's own retry after a lost answer, or the
 * caller's) whose first request made its write, and the answer to that request cannot be given
 * again, for one of three reasons: it was too large for the API to keep whole, the request failed
 * with a 5xx after the write was made, which `answer.status` then names, or the answer carried a
 * credential shown only once, which the API never keeps. Every repeat of the key is refused the
 * same way, so the client never retries this one, and sending the write again with a new key
 * would make it twice. Read the resource instead: at `answer.location` where the write created
 * one, and with `answer.etag` as the `ifMatch` of the next write once it has been read. A
 * credential that cannot be shown again is revoked rather than created a second time beside it.
 *
 * A {@link MountayaConflictError} by its status, so a caller handling every 409 alike still meets
 * it, and a class of its own so that one handling a conflict can tell a write that was made from
 * one that was refused.
 */
export class MountayaIdempotentAnswerUnavailableError extends MountayaConflictError {
  override readonly name = "MountayaIdempotentAnswerUnavailableError";

  /** What the write's first answer was, as the metadata states it: see {@link IdempotentAnswer}. */
  readonly answer: IdempotentAnswer;

  /**
   * @param message - the message {@link failureMessage} spells for the call.
   * @param status - the HTTP status the response carried, which is 409.
   * @param errors - the envelope's entries, the first of which is coded
   *   `IDEMPOTENCY_ANSWER_UNAVAILABLE`.
   * @param details - what the answer sent beside them, the first answer at `metadata` included.
   */
  constructor(
    message: string,
    status: number,
    errors: readonly ErrorEntry[],
    details: ApiErrorDetails = {},
  ) {
    super(message, status, errors, details);

    // Each member read for itself and kept only in the type the contract declares it in, so a
    // metadata object the API did not write reads as the members it does state.
    const { etag: taggedAs, location: createdAt, status: answeredWith } = details.metadata ?? {};
    this.answer = {
      etag: typeof taggedAs === "string" ? taggedAs : undefined,
      location: typeof createdAt === "string" ? createdAt : undefined,
      status:
        typeof answeredWith === "number" && Number.isInteger(answeredWith)
          ? answeredWith
          : undefined,
    };
  }
}

/**
 * 412: a conditional write whose `ifMatch` names a tag the resource no longer carries. Somebody
 * changed it since it was read, and nothing was written. It is never repeated: read the resource
 * again, and decide what the write should be now.
 */
export class MountayaPreconditionFailedError extends MountayaApiError {
  override readonly name = "MountayaPreconditionFailedError";

  /**
   * The tag the resource carries now, from the 412's `ETag`, exactly as sent, or undefined where
   * the answer carried none. It is what the resource was changed to, not what the caller has read:
   * read the resource again before a write names it as its `ifMatch`, so the write is decided
   * against what it holds.
   */
  readonly etag?: string | undefined;

  /**
   * @param message - the message {@link failureMessage} spells for the call.
   * @param status - the HTTP status the response carried, which is 412.
   * @param errors - the envelope's entries, the first of which classifies the response.
   * @param details - what the answer sent beside them, its `ETag` included.
   */
  constructor(
    message: string,
    status: number,
    errors: readonly ErrorEntry[],
    details: ApiErrorDetails = {},
  ) {
    super(message, status, errors, details);
    this.etag = details.etag;
  }
}

/** 413: the request body exceeds the accepted size. */
export class MountayaPayloadTooLargeError extends MountayaApiError {
  override readonly name = "MountayaPayloadTooLargeError";
}

/**
 * 429: a limit is spent: a per-minute limit of the Tile or Data API, a monthly allowance there,
 * one of the Map Studio API's own limits (a key's reads, imports and previews, routed writes,
 * agent runs, collaboration signals, stream opens, invitations, `postSession`,
 * `postOrganizationLookup`), or the signed download URL an export answers with.
 *
 * `retryAfter` is seconds, from the `Retry-After` every 429 carries, the client's own retries
 * waiting it out where it fits the call's `retry.maxWaitMs`. A per-minute limit names the
 * seconds left in its minute, which the client waits out within its budget before it asks again.
 * A spent monthly allowance names the seconds until the next month begins in UTC, a wait past any
 * budget, so the error arrives at once and carries it. Each surface's limits are its own, so a
 * caller that hits this on one surface has learned nothing about its headroom on another.
 */
export class MountayaRateLimitError extends MountayaApiError {
  override readonly name = "MountayaRateLimitError";
}

/** 5xx: the API failed. These carry no server detail, by design. */
export class MountayaServerError extends MountayaApiError {
  override readonly name = "MountayaServerError";
}

/**
 * MountayaDataError is a Data API failure the API reported in a GraphQL envelope carried by a
 * SUCCESS: a 200 whose `errors` say the operation did not happen, or one with no data at all.
 *
 * It is the one member of this family named for a TRANSPORT rather than for a status, which
 * is why it sits after the status ladder instead of inside it. A Data API failure answered with
 * a non-2xx status is NOT this class: it climbs the same ladder a REST failure does, so a 401 is
 * a {@link MountayaAuthenticationError}, a 402 a {@link MountayaBillingError}, a 429 a
 * {@link MountayaRateLimitError} and a 5xx a {@link MountayaServerError}, carrying the
 * envelope's entries. A caller handling the ladder handles both surfaces. This class is what is
 * left: GraphQL puts a field's failure in the body of a 200, which no status can classify. It
 * subclasses {@link MountayaApiError} because the envelope entries are the same
 * `{message, path, extensions.code}` the REST surface uses (the API renders one error into
 * both), so a caller that already handles `MountayaApiError` handles this too.
 */
export class MountayaDataError extends MountayaApiError {
  override readonly name = "MountayaDataError";
}

/**
 * MOUNTAYA_API_ERROR_NAMES holds the name each failure the API answered declares:
 * {@link MountayaApiError}'s, and that of each class a status or a Data API envelope maps to.
 */
export const MOUNTAYA_API_ERROR_NAMES: readonly string[] = [
  "MountayaApiError",
  "MountayaAuthenticationError",
  "MountayaBadRequestError",
  "MountayaBillingError",
  "MountayaConflictError",
  "MountayaDataError",
  "MountayaForbiddenError",
  "MountayaIdempotencyKeyInUseError",
  "MountayaIdempotentAnswerUnavailableError",
  "MountayaMethodNotAllowedError",
  "MountayaNotFoundError",
  "MountayaPayloadTooLargeError",
  "MountayaPreconditionFailedError",
  "MountayaRateLimitError",
  "MountayaServerError",
];

/**
 * MOUNTAYA_ERROR_NAMES holds the name each error of the family declares: the failures the API
 * answered, and those no answer came with, the event-stream parser's among them.
 *
 * Spelled as each class declares its name, the `Mountaya` prefix included and
 * `SseBufferOverflowError` without one, because a thrown value carries that spelling and no other.
 * `errors.test.ts` derives the family from the published barrels and holds this list to it in
 * both directions.
 */
export const MOUNTAYA_ERROR_NAMES: readonly string[] = [
  ...MOUNTAYA_API_ERROR_NAMES,
  "MountayaConfigurationError",
  "MountayaConnectionError",
  "MountayaError",
  "MountayaSelectionError",
  "SseBufferOverflowError",
];

/**
 * declaresName reports whether a value is an object whose `name`, read as data, is one of
 * `names`. A class is refused, though its own `name` spells the class: it is not an error.
 */
const declaresName = (
  value: unknown,
  names: readonly string[],
): value is { readonly name: string } =>
  typeof value === "object" &&
  value !== null &&
  "name" in value &&
  typeof value.name === "string" &&
  names.includes(value.name);

/**
 * isMountayaError reports whether a value is an error of this family, read from the name its
 * class declares rather than from its constructor.
 *
 * Structural, so an error thrown by a second copy of this package still classifies: a package
 * resolved once as ESM and once as CJS, or installed twice, loads two class objects, and
 * `instanceof` against one refuses every error the other throws. Each class in the family
 * declares its name as a literal for this reading (see {@link MountayaError}). A value whose name
 * is none of the family's is none of its errors, whatever status it carries.
 *
 * It answers for the whole family, a timeout and a refused configuration included, so what it
 * narrows to is the base: narrow further with {@link isMountayaApiError} before reading `status`,
 * `code` or `errors`.
 *
 * @example
 * ```ts
 * try {
 *   await mountaya.studio.itineraries.getItinerary({ itinerary_id });
 * } catch (error) {
 *   if (!isMountayaError(error)) {
 *     throw error; // A defect in the calling program, not a failure of the call.
 *   }
 *   console.warn(error.message);
 * }
 * ```
 */
export const isMountayaError = (error: unknown): error is MountayaError =>
  declaresName(error, MOUNTAYA_ERROR_NAMES);

/**
 * isMountayaApiError reports whether a value is a failure the API answered: an error of the
 * family whose class a status maps to, carrying what that class carries, so that everything the
 * narrowed type promises is there to read whichever copy of this package built it.
 *
 * Narrow with it before reading `status`, `code`, `errors` or `metadata`, or calling `hasCode`,
 * none of which a timeout, a dropped connection or a refused configuration carries. Structural,
 * for the reason {@link isMountayaError} gives: it reads the name the class declares, the numeric
 * `status`, the `errors` array with each entry an object, and the `hasCode` method the class
 * adds, and never the constructor.
 *
 * A value revived from JSON is refused. It holds the data and none of what the class adds: on it
 * `code` reads `undefined` and `hasCode` is no function, so narrowing it to the class would be
 * false. Read such a value as the plain data it is, through its `errors` member, and classify it
 * by its `name` with {@link isMountayaError}, which promises the name alone.
 *
 * @example
 * ```ts
 * try {
 *   await mountaya.studio.itineraries.getItinerary({ itinerary_id });
 * } catch (error) {
 *   if (isMountayaApiError(error) && error.hasCode("NOT_FOUND")) {
 *     console.warn(error.status, error.errors[0]?.message);
 *   }
 * }
 * ```
 */
export const isMountayaApiError = (error: unknown): error is MountayaApiError =>
  declaresName(error, MOUNTAYA_API_ERROR_NAMES) &&
  "status" in error &&
  typeof error.status === "number" &&
  "errors" in error &&
  Array.isArray(error.errors) &&
  error.errors.every((entry: unknown) => typeof entry === "object" && entry !== null) &&
  "hasCode" in error &&
  typeof error.hasCode === "function";

/**
 * isErrorEntry narrows one member of an envelope's `errors` to an entry of the API's error
 * envelope: an object carrying a string `message`.
 *
 * Hand-written rather than schema-validated because this package ships zero runtime
 * dependencies, and because the parse must be forward-compatible: an entry missing
 * `extensions` or carrying fields this release has never heard of is still a valid entry,
 * so only `message` is required. A stricter parse would downgrade a specific, actionable
 * message of the API to a bare status code the first time the envelope grew a field.
 */
const isErrorEntry = (entry: unknown): entry is ErrorEntry =>
  typeof entry === "object" &&
  entry !== null &&
  "message" in entry &&
  typeof entry.message === "string";

/**
 * errorsOf reads the `errors` member of a parsed body as it parsed, or undefined where the body
 * is no object or carries none.
 */
const errorsOf = (body: unknown): unknown =>
  typeof body === "object" && body !== null && "errors" in body ? body.errors : undefined;

/**
 * entriesOf reads the entries of an error envelope off a parsed body, whatever the body parsed
 * to: each member of its `errors` list that is an object carrying a message, in the order sent,
 * and none where `errors` is no list.
 *
 * One reading for every surface, one entry at a time. A REST or Tile API refusal and a Data API
 * envelope are both read through it before anything reads an entry, so a body something on the
 * network path wrote (`{"errors":"bad gateway"}`, `{"errors":[null]}`) classifies by its status
 * rather than throwing a `TypeError` from the first property read, and a member that is no entry
 * costs none of the entries beside it.
 */
export const entriesOf = (body: unknown): readonly ErrorEntry[] => {
  const errors = errorsOf(body);

  return Array.isArray(errors) ? errors.filter(isErrorEntry) : [];
};

/**
 * statesErrors reports whether a parsed body's `errors` member holds anything: it is present and
 * is neither `null` nor an empty list, whether or not what it holds is an entry
 * {@link entriesOf} reads.
 *
 * It is what makes a `200` from the Data API a failure. An answer that states errors did not do
 * what was asked, and one whose `errors` hold no entry to read (a string, a list of `null`) says
 * so just as surely: read by its entries alone, it would be handed to the caller as a success.
 */
export const statesErrors = (body: unknown): boolean => {
  const errors = errorsOf(body);

  return errors !== undefined && errors !== null && !(Array.isArray(errors) && errors.length === 0);
};

/**
 * isMetadata reports whether a value can stand as an envelope's metadata: an object that is
 * neither null nor an array, which every metadata shape the contract declares is.
 */
const isMetadata = (value: unknown): value is ErrorMetadata =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * metadataOf reads an envelope's `extensions.metadata` off a parsed body, or undefined where the
 * body carries none.
 *
 * Read apart from the entries, because a failure can carry one without the other: a `409` for a
 * held lock carries both, and an answer with no entries still classifies by its status. A value
 * that is not an object is no metadata this contract declares, and is left out rather than
 * handed on under a type it does not have.
 */
export const metadataOf = (body: unknown): ErrorMetadata | undefined => {
  if (typeof body !== "object" || body === null || !("extensions" in body)) {
    return undefined;
  }

  const { extensions } = body;
  if (typeof extensions !== "object" || extensions === null || !("metadata" in extensions)) {
    return undefined;
  }

  const { metadata } = extensions;

  return isMetadata(metadata) ? metadata : undefined;
};

/**
 * PAYMENT_REQUIRED_CODE is the code of a 402, the status {@link MountayaBillingError} stands for:
 * a product the organization does not hold, a call past what its agreement covers, or, on the
 * Data API, a self-serve organization with no payment method on file or with invoices still owed.
 * A 402 refusing a ceiling of the agreement carries `LIMIT_REACHED` in its place.
 */
export const PAYMENT_REQUIRED_CODE = "PAYMENT_REQUIRED";

/**
 * What a 402 tells a caller: where each of its remedies is taken, with the API's message naming
 * which one applies.
 *
 * Every remedy is named because the status alone does not say which fact the API refused for,
 * and choosing one here would send a reader with no payment method on file, or with an invoice
 * still owed, to change an agreement that already covers the call, or a reader past a ceiling to
 * a settings page that cannot lift it. The API's own message names the fact.
 */
const BILLING_STEP =
  "The API's message says which applies: a product or a ceiling of the organization's agreement is changed with Mountaya (https://mountaya.com/en/contact/), and a missing payment method is added, or an invoice still owed paid, in the organization's billing settings.";

/** What a 401 asks a caller to check, whatever the call. */
const CREDENTIAL_STEP =
  "Check the credential this client sends: a key that is unknown, rotated or disabled, and a session token that has expired, are refused.";

/**
 * What a 401 adds for a call that acts as a member: its session token has to name the user it
 * acts as, which a token minted without `user_id` does not, and the API answers that token as it
 * answers none.
 */
const MEMBER_SESSION_STEP =
  "This call acts as the user its session token was minted for, so mint the token with postSession's user_id set to that user.";

/** What a 401 on `postSession` asks a caller to check: the secret key, which it alone takes. */
const SECRET_KEY_STEP =
  "Check the secret key this client sends: one that is unknown, revoked or malformed, or one of a canceled organization, is refused.";

/** What a 403 asks a caller to check, whatever the call. */
const FORBIDDEN_STEP =
  "Check that the key carries the scope this call needs, that the resource belongs to the key's organization, and that the page calling is one the organization allows.";

/**
 * What a 403 adds for a call that acts as a member: the user its session token names has to be a
 * member of the key's organization holding the role the call asks.
 */
const ACTING_USER_STEP =
  "This call acts as the user its session token names (postSession's user_id), who must be a member of the key's organization holding the role the call asks.";

/**
 * What a 403 on `postSession` asks a caller to check: the publishable key it names, and the user
 * it mints for. A user who is not a member, and one who does not exist, answer alike.
 */
const MINT_REFUSAL_STEP =
  "Check that the publishable key in the body is one this secret key's organization issued, and that user_id names a member of that organization or a user holding an invitation to it.";

/**
 * What a 412 asks a caller to do: the tag the write named is stale, so the write is not repeated
 * as it is.
 */
const PRECONDITION_STEP =
  "The resource changed since the etag this write names was read: read it again, and decide what the write should be now.";

/**
 * What the 409 coded `IDEMPOTENCY_ANSWER_UNAVAILABLE` asks a caller to do: the write was made, so
 * it is read again rather than sent again.
 */
const IDEMPOTENT_ANSWER_STEP =
  "The write was made, and its answer cannot be given again: read the resource again rather than sending the write once more.";

/**
 * What the 409 coded `IDEMPOTENCY_KEY_IN_USE` asks a caller to do once the client stopped asking:
 * the first request with the key is still running, so the call is sent again with the same key.
 */
const KEY_IN_USE_STEP =
  "The first request with this Idempotency-Key is still running: send the same call again with the error's idempotencyKey, never a new key, which would make the write twice.";

/**
 * What a 429 on `postSession` adds: the limits it keeps, the last of which a `user_id` the API
 * refuses runs into on its own, so a caller minting for somebody who left meets it while minting
 * for everybody else still works.
 */
const MINT_LIMIT_STEP =
  "Minting is limited per secret key, per calling address and secret key whose requests keep failing, and per user_id it refuses, so check the publishable key and the user_id before asking again.";

/**
 * FailureDeclaration is what a failed call's operation declares about the credentials it takes,
 * as far as the step its refusal names turns on it. Each member is the `RequestArgs` member of the
 * same name, so a request descriptor is one, and an absent member is a declaration not made.
 */
export interface FailureDeclaration {
  /** Whether the operation acts as the user its session token names (`x-acting-user: required`). */
  readonly actsAsMember?: boolean | undefined;
  /** Whether the operation is authenticated by a secret key, as `postSession` alone is. */
  readonly requiresSecretKey?: boolean | undefined;
}

/**
 * nextStepFor names what a caller can do about a failure, for the statuses that name one.
 *
 * A 401, 402, 403, 412 and 429 each have a remedy on the caller's side, and so do the 409 coded
 * `IDEMPOTENCY_ANSWER_UNAVAILABLE`, whose write was made, and the one coded
 * `IDEMPOTENCY_KEY_IN_USE`, whose first request is still running; a message that stops at the
 * status leaves the reader of a log to work it out. Every other status says nothing a caller can act on beyond
 * the API's own message: a 400 or another 409 is about this request, which the entries describe,
 * and a 5xx is the API's.
 *
 * What the operation declares sharpens the step. A call that acts as a member is told which user
 * its session token has to name, and `postSession`, authenticated by a secret key, is told about
 * the secret key, the publishable key and the user it mints for.
 */
const nextStepFor = (
  status: number,
  code: unknown,
  retryAfterSeconds: number | undefined,
  declaration: FailureDeclaration,
): string | undefined => {
  const mints = declaration.requiresSecretKey === true;
  const actsAsMember = declaration.actsAsMember === true;

  switch (status) {
    case 401:
      if (mints) {
        return SECRET_KEY_STEP;
      }

      return actsAsMember ? `${CREDENTIAL_STEP} ${MEMBER_SESSION_STEP}` : CREDENTIAL_STEP;
    case 402:
      return BILLING_STEP;
    case 403:
      if (mints) {
        return MINT_REFUSAL_STEP;
      }

      return actsAsMember ? `${FORBIDDEN_STEP} ${ACTING_USER_STEP}` : FORBIDDEN_STEP;
    case 409:
      return code === IDEMPOTENT_ANSWER_UNAVAILABLE_CODE
        ? IDEMPOTENT_ANSWER_STEP
        : code === KEY_IN_USE_CODE
          ? KEY_IN_USE_STEP
          : undefined;
    case 412:
      return PRECONDITION_STEP;
    case 429: {
      // A wait of none is still a wait for the limit's window, so it is named without a number.
      const wait =
        retryAfterSeconds === undefined || retryAfterSeconds === 0
          ? "Wait before calling again."
          : `Wait ${retryAfterSeconds} ${retryAfterSeconds === 1 ? "second" : "seconds"} before calling again.`;

      return mints ? `${wait} ${MINT_LIMIT_STEP}` : wait;
    }
    default:
      return undefined;
  }
};

/**
 * FailureFacts are what a failure's message reads beyond its status and its entries, each where
 * the answer or the call states it.
 */
export interface FailureFacts {
  /** What the call's operation declares, which sharpens the next step. */
  readonly declaration?: FailureDeclaration | undefined;
  /** The answer's `X-Request-Id`, which the message ends on. */
  readonly requestId?: string | undefined;
  /** The whole seconds a 429's `Retry-After` names, which its next step spells. */
  readonly retryAfterSeconds?: number | undefined;
}

/**
 * failureMessage spells what a failed call reports: `<context>: <status> <CODE> <message>`, then
 * the next step a caller can take where the status names one, then the request id where the
 * answer named one: `getItinerary: 404 NOT_FOUND Itinerary not found. (request 4bf9…)`.
 *
 * The code and the message are the first entry's, which classifies the response as a whole, and
 * each part is left out when the response did not carry it: a bodiless 502 reads
 * `getItinerary: 502`. `status` is omitted for a Data API field failure, whose 200 says nothing
 * about it. The message ends at the API's words, and the next step, when there is one,
 * follows as a sentence of its own, sharpened by what the call's operation declares.
 */
export const failureMessage = (
  context: string,
  status: number | undefined,
  entries: readonly ErrorEntry[],
  facts: FailureFacts = {},
): string => {
  // Read as unknown: an entry is held to an object carrying a message and to nothing else, so a
  // code or a message that is not a string is left out rather than printed as `[object Object]`.
  const first: {
    readonly extensions?: { readonly code?: unknown } | undefined;
    readonly message?: unknown;
  } = entries[0] ?? {};
  const parts = [status, first.extensions?.code, first.message]
    .map((part) =>
      typeof part === "string" ? part.trim() : typeof part === "number" ? String(part) : "",
    )
    .filter((part) => part !== "");
  const summary = parts.length === 0 ? context : `${context}: ${parts.join(" ")}`;
  const next =
    status === undefined
      ? undefined
      : nextStepFor(
          status,
          first.extensions?.code,
          facts.retryAfterSeconds,
          facts.declaration ?? {},
        );
  const sentence =
    next === undefined ? summary : `${/[.!?]$/.test(summary) ? summary : `${summary}.`} ${next}`;

  return facts.requestId === undefined ? sentence : `${sentence} (request ${facts.requestId})`;
};

/** The month names an HTTP date spells, in calendar order. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The whole-seconds form: digits alone, with the optional whitespace a field value may carry. */
const delaySecondsPattern = /^[ \t]*(\d+)[ \t]*$/;

/** IMF-fixdate, the form a sender writes today: `Sun, 06 Nov 1994 08:49:37 GMT`. */
const imfFixdatePattern =
  /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), (\d{2}) ([A-Z][a-z]{2}) (\d{4}) (\d{2}):(\d{2}):(\d{2}) GMT$/;

/** The obsolete RFC 850 form, with a two-digit year: `Sunday, 06-Nov-94 08:49:37 GMT`. */
const rfc850DatePattern =
  /^(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday), (\d{2})-([A-Z][a-z]{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2}) GMT$/;

/** The obsolete asctime form, in UTC though it says nothing: `Sun Nov  6 08:49:37 1994`. */
const asctimeDatePattern =
  /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun) ([A-Z][a-z]{2}) ([ \d]\d) (\d{2}):(\d{2}):(\d{2}) (\d{4})$/;

/**
 * utcInstant turns the fields of an HTTP date into epoch milliseconds, or undefined where they
 * name no real instant: a month outside the list, a day the month does not have, an hour past
 * 23. A second of 60 is the leap second the grammar allows.
 */
const utcInstant = (
  year: number,
  month: string,
  day: number,
  hour: number,
  minute: number,
  second: number,
): number | undefined => {
  const monthIndex = MONTHS.indexOf(month);
  if (monthIndex < 0 || hour > 23 || minute > 59 || second > 60) {
    return undefined;
  }

  // Checked against the calendar rather than a table, so 29 February is right in a leap year.
  // `setUTCFullYear` rather than `Date.UTC`, which reads a year below 100 as one in the 1900s.
  const midnight = new Date(0);
  midnight.setUTCFullYear(year, monthIndex, day);
  if (midnight.getUTCMonth() !== monthIndex || midnight.getUTCDate() !== day) {
    return undefined;
  }

  return midnight.getTime() + ((hour * 60 + minute) * 60 + second) * 1000;
};

/**
 * httpDate reads an HTTP date in any of its three forms, strictly, as epoch milliseconds.
 *
 * Strictly, because the runtime's own `Date.parse` reads far more than the grammar allows, and
 * reads it differently per engine: V8 takes an ISO-8601 timestamp, a date with no weekday, and
 * even `"-5"`, which it reads as a date in 2001. The names are case-sensitive, as the grammar
 * says; the weekday is read and not checked against the date.
 *
 * `now` places the RFC 850 form's two-digit year, which is read in the current century, or in the
 * one before where the current century's reading is more than 50 years ahead: the rule RFC 9110
 * (section 5.6.7) gives a recipient.
 */
const httpDate = (value: string, now: number): number | undefined => {
  const fixdate = imfFixdatePattern.exec(value);
  if (fixdate !== null) {
    const [, day, month, year, hour, minute, second] = fixdate;

    return utcInstant(
      Number(year),
      month ?? "",
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
  }

  const rfc850 = rfc850DatePattern.exec(value);
  if (rfc850 !== null) {
    const [, day, month, shortYear, hour, minute, second] = rfc850;
    const currentYear = new Date(now).getUTCFullYear();
    const sameDigits = currentYear - (currentYear % 100) + Number(shortYear);
    const year = sameDigits > currentYear + 50 ? sameDigits - 100 : sameDigits;

    return utcInstant(year, month ?? "", Number(day), Number(hour), Number(minute), Number(second));
  }

  const asctime = asctimeDatePattern.exec(value);
  if (asctime !== null) {
    const [, month, day, hour, minute, second, year] = asctime;

    return utcInstant(
      Number(year),
      month ?? "",
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
  }

  return undefined;
};

/**
 * parseRetryAfter reads the `Retry-After` header as whole seconds, or undefined where it says
 * nothing this client can use.
 *
 * The header is defined as either a delay in seconds or an HTTP date, and both forms appear in
 * the wild behind proxies, so both are handled, and nothing else is: a blank, a sign, a
 * fraction, an exponent or a hex prefix is ignored, and the backoff schedule applies as if no
 * header had come. The grammar is matched rather than the value coerced, because `Number` reads
 * each of those as a delay (a blank as zero seconds, `"1e3"` as a thousand). A date in the past
 * clamps to 0 rather than producing a negative delay a backoff would treat as an error.
 *
 * `now` is a parameter with a default because the date form is a SUBTRACTION from the
 * current instant, and a rule whose answer moves with the wall clock is one no fixture can
 * pin and no second language can be held to. Every caller takes the default; the conformance
 * suite passes a fixed instant, which is the only way the date branch has an expected value
 * at all.
 */
export const parseRetryAfter = (
  header: string | null,
  now: () => number = Date.now,
): number | undefined => {
  if (header === null) {
    return undefined;
  }

  const digits = delaySecondsPattern.exec(header)?.[1];
  if (digits !== undefined) {
    // A delay past what a double holds exactly is read as the greatest one it does, which is
    // still a wait no retry sits out.
    return Math.min(Number(digits), Number.MAX_SAFE_INTEGER);
  }

  const current = now();
  const instant = httpDate(header.replace(/^[ \t]+|[ \t]+$/g, ""), current);

  return instant === undefined ? undefined : Math.max(0, Math.round((instant - current) / 1000));
};

/**
 * answerDetailsOf reads what an answer's headers say about it beside its body: its request id
 * and the rate-limit state it describes. A request id that is empty or whitespace alone is none.
 */
export const answerDetailsOf = (
  headers: Pick<Headers, "get">,
): Pick<ApiErrorDetails, "rateLimit" | "requestId"> => {
  const requestId = headers.get("X-Request-Id")?.trim();

  return {
    rateLimit: parseRateLimit(headers),
    requestId: requestId === undefined || requestId === "" ? undefined : requestId,
  };
};

/**
 * replayedOf reports whether an answer is one the API kept for an `Idempotency-Key` it had seen
 * and gave again: it carries `Idempotent-Replayed: true`, read without regard to case. Every other
 * value, and no header at all, is an answer the API made for this request.
 */
export const replayedOf = (headers: Pick<Headers, "get">): boolean =>
  headers.get("Idempotent-Replayed")?.trim().toLowerCase() === "true";

/**
 * errorOfBody builds the typed error for a non-2xx answer from its parsed body, whatever the
 * body parsed to: the one place a refusal's entries, metadata, `Retry-After`, `ETag`, request id
 * and rate-limit state become an error, beside the `Idempotency-Key` the call sent.
 */
const errorOfBody = (
  body: unknown,
  answer: Pick<Response, "headers" | "status">,
  context: string,
  declaration: FailureDeclaration | undefined,
  idempotencyKey: string | undefined,
): MountayaApiError => {
  const entries = entriesOf(body);
  const retryAfterSeconds = parseRetryAfter(answer.headers.get("Retry-After"));
  const details = answerDetailsOf(answer.headers);

  return errorForStatus(
    failureMessage(context, answer.status, entries, {
      declaration,
      requestId: details.requestId,
      retryAfterSeconds,
    }),
    answer.status,
    entries,
    {
      ...details,
      etag: answer.headers.get("ETag") ?? undefined,
      idempotencyKey,
      metadata: metadataOf(body),
      retryAfter: retryAfterSeconds,
    },
  );
};

/**
 * errorFromResponse builds the right typed error for a non-2xx response.
 *
 * The body is read once and tolerated when absent or unparseable: a bodiless 502 from a proxy is
 * still a real error and still deserves its class. `context` names the operation, and the
 * message is {@link failureMessage}'s: `getItinerary: 403 FORBIDDEN Access is forbidden.`
 * followed by the next step a 401, 402, 403, 412 or 429 asks for, or a 409 that answers the
 * call's `Idempotency-Key`, sharpened by what the call's operation `declaration` states, and the
 * request id. The error carries the envelope's entries, its {@link ErrorMetadata}, the request
 * id and the rate-limit state, each where the answer sent it, and the `idempotencyKey` the call
 * sent, where it sent one.
 */
export const errorFromResponse = async (
  response: Response,
  context: string,
  declaration?: FailureDeclaration,
  idempotencyKey?: string,
): Promise<MountayaApiError> =>
  errorOfBody(
    await response.json().catch(() => undefined),
    response,
    context,
    declaration,
    idempotencyKey,
  );

/**
 * errorFromBody builds the typed error {@link errorFromResponse} builds, for an answer whose
 * body is already read, as the text it arrived as.
 *
 * It takes the status and the headers off `answer` and never builds a `Response` to carry them,
 * so a status the `Response` constructor refuses (a 304, or one outside 200 to 599, which a
 * runtime hands back as a server sent it) classifies like any other. Text that is not JSON is no
 * envelope, and the status alone decides the class.
 */
export const errorFromBody = (
  text: string,
  answer: Pick<Response, "headers" | "status">,
  context: string,
  declaration?: FailureDeclaration,
  idempotencyKey?: string,
): MountayaApiError => {
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    body = undefined;
  }

  return errorOfBody(body, answer, context, declaration, idempotencyKey);
};

/**
 * errorForStatus picks the class a status maps to, for a caller holding a status and no
 * `Response` to read.
 *
 * The Data API is that caller. It has a response, but one whose body is a GraphQL envelope
 * already decoded, and a non-2xx one is classified here exactly as a REST response is: the same
 * status decides the same class on every surface. The alternative is a bespoke error class
 * beside the family, which is exactly the duplication `namespaces.test.ts` asserts against.
 * Split out of {@link errorFromResponse} rather than copied, so the two cannot disagree about
 * which class a 402 is.
 *
 * @param message - The error's message, as {@link failureMessage} spells one.
 * @param status - The HTTP status the response carried.
 * @param entries - The envelope's entries, none where omitted. The status alone decides the
 *   class, a 402's included, whatever code an entry carries.
 * @param details - What the answer sent beside them: the envelope's metadata, the request id, the
 *   rate-limit state, the `Idempotency-Key` the call sent, the wait its `Retry-After` names as
 *   `retryAfter`, and the tag a 412 carries as its `etag`, which every other status leaves out.
 * @returns The class the status maps to: a {@link MountayaServerError} for any 5xx, and a
 *   plain {@link MountayaApiError} for a status no class is named for.
 */
export const errorForStatus = (
  message: string,
  status: number,
  entries: readonly ErrorEntry[] = [],
  details: ApiErrorDetails = {},
): MountayaApiError => {
  // The tag is a 412's alone: a success's is the resource's, and a refusal names no tag but the
  // one a stale write meets. The wait rides every class whose answer named one.
  const { etag, ...rest } = details;
  const args = [message, status, entries, rest] as const;

  switch (status) {
    case 400:
      return new MountayaBadRequestError(...args);
    case 401:
      return new MountayaAuthenticationError(...args);
    case 402:
      return new MountayaBillingError(...args);
    case 403:
      return new MountayaForbiddenError(...args);
    case 404:
      return new MountayaNotFoundError(...args);
    case 405:
      return new MountayaMethodNotAllowedError(...args);
    case 409:
      // The status decides the class, and two 409s' entry decides a subclass: each answers the
      // call's key rather than its write, and one says the write was made, which is the one thing
      // a caller must never read as a conflict.
      switch (entries[0]?.extensions?.code) {
        case IDEMPOTENT_ANSWER_UNAVAILABLE_CODE:
          return new MountayaIdempotentAnswerUnavailableError(...args);
        case KEY_IN_USE_CODE:
          return new MountayaIdempotencyKeyInUseError(...args);
        default:
          return new MountayaConflictError(...args);
      }
    case 412:
      return new MountayaPreconditionFailedError(message, status, entries, { ...rest, etag });
    case 413:
      return new MountayaPayloadTooLargeError(...args);
    case 429:
      return new MountayaRateLimitError(...args);
    default:
      return status >= 500 ? new MountayaServerError(...args) : new MountayaApiError(...args);
  }
};
